import { useNavigate } from "react-router-dom";
import Header from "./Header";
import { useAccessibility } from "../context/AccessibilityContext";
import "./AccessibilitySettings.css";

const TEXT_SIZES = [
    { value: 0, key: "a11y-text-normal", fallback: "Normal" },
    { value: 1, key: "a11y-text-large", fallback: "Large" },
    { value: 2, key: "a11y-text-larger", fallback: "Larger" },
    { value: 3, key: "a11y-text-largest", fallback: "Largest" },
];

const COLORBLIND_OPTIONS = [
    { value: "none", key: "a11y-cb-off", fallback: "Off" },
    { value: "protanopia", key: "a11y-cb-protanopia", fallback: "Protanopia (red-blind)" },
    { value: "deuteranopia", key: "a11y-cb-deuteranopia", fallback: "Deuteranopia (green-blind)" },
    { value: "tritanopia", key: "a11y-cb-tritanopia", fallback: "Tritanopia (blue-blind)" },
];

export default function AccessibilitySettings({ translations }) {
    const navigate = useNavigate();
    const { settings, setSetting, reset } = useAccessibility();
    const t = (key, fallback) => translations?.[key] || fallback;

    const Toggle = ({ id, settingKey, label }) => (
        <div className="a11y-row">
            <span className="a11y-label">{label}</span>
            <input
                className="checkbox-dark-mode"
                type="checkbox"
                id={id}
                checked={!!settings[settingKey]}
                onChange={(e) => setSetting(settingKey, e.target.checked)}
            />
            <label className="dark-mode" htmlFor={id}></label>
        </div>
    );

    return (
        <>
            <Header translations={translations} />
            <div className="a11y-container">
                <button className="a11y-back" onClick={() => navigate("/profile")}>
                    ← {t("a11y-back", "Back to profile")}
                </button>
                <h1 className="a11y-title">{t("a11y-title", "Accessibility")}</h1>
                <p className="a11y-subtitle">
                    {t("a11y-subtitle", "Adjust how ArtSyde looks. Changes apply instantly and are saved on this device.")}
                </p>

                <section className="a11y-section">
                    <h2 className="a11y-section-title">{t("a11y-text-size", "Text size")}</h2>
                    <div className="a11y-segmented" role="group" aria-label={t("a11y-text-size", "Text size")}>
                        {TEXT_SIZES.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`a11y-segment ${settings.textSize === opt.value ? "active" : ""}`}
                                onClick={() => setSetting("textSize", opt.value)}
                                aria-pressed={settings.textSize === opt.value}
                            >
                                {t(opt.key, opt.fallback)}
                            </button>
                        ))}
                    </div>
                </section>

                <section className="a11y-section">
                    <Toggle
                        id="a11y-contrast-toggle"
                        settingKey="highContrast"
                        label={t("a11y-high-contrast", "High contrast")}
                    />
                    <Toggle
                        id="a11y-motion-toggle"
                        settingKey="reduceMotion"
                        label={t("a11y-reduce-motion", "Reduce motion")}
                    />
                    <Toggle
                        id="a11y-transparency-toggle"
                        settingKey="noTransparency"
                        label={t("a11y-no-transparency", "Remove transparency")}
                    />
                    <Toggle
                        id="a11y-bold-toggle"
                        settingKey="boldText"
                        label={t("a11y-bold-text", "Bold text")}
                    />
                </section>

                <section className="a11y-section">
                    <h2 className="a11y-section-title">{t("a11y-colorblind", "Colorblind filter")}</h2>
                    <select
                        className="a11y-select"
                        value={settings.colorBlind}
                        onChange={(e) => setSetting("colorBlind", e.target.value)}
                        aria-label={t("a11y-colorblind", "Colorblind filter")}
                    >
                        {COLORBLIND_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {t(opt.key, opt.fallback)}
                            </option>
                        ))}
                    </select>
                </section>

                <button className="a11y-reset" onClick={reset}>
                    {t("a11y-reset", "Reset to defaults")}
                </button>
            </div>
        </>
    );
}
